<template>
	<el-scrollbar>
		<div v-if="widgetForm.list?.length === 0" class="maku-tips">{{ $t('designForm.dragTip') }}</div>
		<el-form :label-position="widgetForm.config.labelPosition" :label-width="widgetForm.config.labelWidth" :size="widgetForm.config.size">
			<Draggable
				class="widget-form-list"
				item-key="name"
				ghost-class="ghost"
				:animation="200"
				handle=".drag-move"
				:list="list"
				group="item"
				@add="handleMoveAdd"
			>
				<template #item="{ element, index }">
					<transition-group name="fade" tag="div">
						<el-row
							v-if="element.type === 'grid'"
							:key="index"
							class="widget-col"
							type="flex"
							:class="{ active: active?.name === element.name }"
							:gutter="element.options.gutter ?? 0"
							:justify="element.options.justify"
							:align="element.options.align"
							@click="handleActive(element)"
						>
							<el-col v-for="(column, columnIndex) of element.columns" :key="columnIndex" :span="column.span ?? 0">
								<Draggable
									class="widget-col-list"
									item-key="name"
									ghost-class="ghost"
									handle=".drag-move"
									:animation="200"
									group="item"
									:no-transition-on-drag="true"
									:list="column.list"
									@add="handleColMoveAdd($event, element, columnIndex)"
								>
									<template #item="{ element, index }">
										<transition-group name="fade" tag="div">
											<WidgetFormItem
												:key="index"
												v-model="element.options.defaultValue"
												:element="element"
												:active-form="active"
												@click.stop="handleActive(element)"
												@copy="handleCopy(index, element, column.list)"
												@delete="handleDelete(index, element, column.list)"
											></WidgetFormItem>
										</transition-group>
									</template>
								</Draggable>
							</el-col>
							<div class="drag-control">
								<div class="drag-move">
									<ma-icon icon="move"></ma-icon>
								</div>
								<div class="item-control">
									<ma-icon icon="delete" :title="$t('designForm.delete')" @click.stop="handleDelete(index, element)"></ma-icon>
								</div>
							</div>
						</el-row>
						<WidgetFormItem
							v-else
							v-model="element.options.defaultValue"
							:element="element"
							:active-form="active"
							@click="handleActive(element)"
							@copy="handleCopy(index, element)"
							@delete="handleDelete(index, element)"
						></WidgetFormItem>
					</transition-group>
				</template>
			</Draggable>
		</el-form>
	</el-scrollbar>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue'
import Draggable from 'vuedraggable'
import WidgetFormItem from './WidgetFormItem.vue'
import { IWidgetForm } from '@/components/design-form/config'
import { getRandom } from '@/utils/tool'
const emits = defineEmits(['update:widgetForm', 'update:activeForm'])

const props = defineProps({
	widgetForm: {
		type: Object as PropType<IWidgetForm>,
		required: true
	},
	activeForm: {
		type: Object,
		default: null
	}
})

const list = computed(() => props.widgetForm.list)
const active = computed(() => props.activeForm)

const handleActive = (item: any) => {
	emits('update:activeForm', item)
}

const handleMoveAdd = (event: any) => {
	const { newIndex } = event

	const item: any = list.value[newIndex]

	// 新增key、name属性
	if (!item.name) {
		Object.assign(item, { key: getRandom(8) })
		Object.assign(item, { name: item.type + '_' + getRandom(6) })
	}

	handleActive(item)
}

const handleColMoveAdd = (event: any, row: any, index: any) => {
	const { newIndex } = event

	const item: any = row.columns[index].list[newIndex]

	// 新增key、name属性
	if (!item.name) {
		Object.assign(item, { key: getRandom(8) })
		Object.assign(item, { name: item.type + '_' + getRandom(6) })
	}

	handleActive(item)
}

const handleCopy = (index: number, item: any, itemList?: any[]) => {
	const newItem = JSON.parse(JSON.stringify(item))
	Object.assign(newItem, { key: getRandom(8) })
	Object.assign(newItem, { name: item.type + '_' + getRandom(6) })
	if (itemList) {
		itemList.push(newItem)
	} else {
		list.value.splice(index + 1, 0, newItem as never)
	}

	handleActive(newItem)
}

const handleDelete = (index: number, item: any, itemList?: any[]) => {
	if (itemList) {
		itemList.splice(index, 1)
	} else {
		list.value.splice(index, 1)
	}

	handleActive(null)
}
</script>

<style lang="scss" scoped>
.maku-tips {
	text-align: center;
	width: 100%;
	font-size: 24px;
	color: rgb(204, 204, 204);
	position: absolute;
	left: 0;
	top: 150px;
}
</style>
